import {
  Card,
  CardButton,
  CardContent,
  CardDivider,
  Flex,
  Icon,
  Text,
  Tooltip,
} from '@bitrise/bitkit';
import { TypeMetricKey } from '@trace/types';
import React, { FunctionComponent, useContext } from 'react';
import { useParams } from 'react-router';

import {
  METRIC_NAME_BY_KEY,
  METRIC_UNIT_BY_KEY,
  METRIC_UNIT_NAME,
} from '../../../constants/Metrics';
import dataAtIds from '../../../dataAtIds.json';
import useTrackingEventTimed from '../../Tracking/useTrackingEventTimed';
import AttributeSelector from '../../AttributeSelector/AttributeSelector';
import { URLStateContext } from '../../URLState/URLState';

interface Props {
  description: string;
  disabled: boolean;
  metric: TypeMetricKey;
}

const PerformanceOverviewCard: FunctionComponent<Props> = (props) => {
  const { description, disabled, children, metric, ...rest } = props;

  const { appId } = useParams<{ appId: string }>();
  const { attributeGroups, push } = useContext(URLStateContext);
  const handlePointerEnter = useTrackingEventTimed('Metric.Interaction', {
    attribute: attributeGroups[metric],
    metric,
  });

  return (
    <Card
      {...rest}
      data-trace-at-id={dataAtIds.PerformanceOverviewVisualisation}
      direction="vertical"
      elevation="x2"
      minWidth="0"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerEnter.cancel}
    >
      <CardContent direction="vertical" gap="x4" grow padding="x4">
        <Flex alignChildrenHorizontal="between" direction="horizontal" gap="x4">
          <Flex direction="horizontal" gap="x2" shrink>
            <Flex shrink>
              <Text ellipsis>{METRIC_NAME_BY_KEY[metric]}</Text>
              <Text config="9" ellipsis textColor="gray-5">
                {METRIC_UNIT_NAME[METRIC_UNIT_BY_KEY[metric]]}
              </Text>
            </Flex>

            <Flex>
              <Tooltip maxWidth="16rem" title={description}>
                {({ ref, ...tooltipRest }) => (
                  <Icon {...tooltipRest} innerRef={ref} name="Info" textColor="gray-5" />
                )}
              </Tooltip>
            </Flex>
          </Flex>

          <Flex>
            <AttributeSelector disabled={disabled} metric={metric} />
          </Flex>
        </Flex>

        <Flex>{children}</Flex>
      </CardContent>

      <CardDivider />

      <CardButton
        data-trace-at-id={dataAtIds.PerformanceOverviewDetailButton}
        disabled={disabled}
        onClick={() => push(`/${appId}/appPerformance/${metric}`)}
      >
        <Text>Details</Text>
        <Icon name="ArrowForward" />
      </CardButton>
    </Card>
  );
};

export default PerformanceOverviewCard;
